import { memo, useCallback, useMemo } from "react";
import {
  Button,
  Col,
  Form,
  OverlayTrigger,
  Row,
  Tooltip,
} from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import PhoneInput from "react-phone-number-input";
import { toast } from "react-toastify";
import { useSaveUser } from "../../../api/mutations/useSaveUser";
import { useGetUser } from "../../../api/queries/useGetUser";
import { useAppContext } from "../../../Context/AppContext";
import { useQueryClient } from "@tanstack/react-query";
import { ageVerificationSchema } from "../../schemas/Schema";
import SelectInput from "../../common/SelectInput";
import { YouthMembershipFormData } from "../../../types/UserData";
import { ImageUrls } from "../../../types/Types";
import { countryOptions, SectionHeader } from "../YouthMembershipForm";
import { useTranslation } from "react-i18next";
import { submitEarlyStageVerification } from "../../../api/earlyStageMembershipApi";
import {
  isEarlyStageMembership,
  isUnder23,
  toDateOnlyString,
} from "../../../utils/membershipTier";

const MAX_ID_FILE_SIZE = 5 * 1024 * 1024;
const VALID_ID_TYPES = [
  "image/png",
  "image/jpeg",
  "image/heif",
  "image/heic",
  "application/pdf",
];
const ID_UPLOAD_TOOLTIP_MSG = "PNG, JPG, JPEG, HEIF, HEIC, PDF, Max 5MB";
const FORM_MIN_HEIGHT = 520;

const renderTooltip = (msg: string) => <Tooltip id="tooltip">{msg}</Tooltip>;

interface FormType {
  dateOfBirth: Date;
  idDocument: any;
  phoneNumber: string | null;
  city: string;
  country: string;
}

interface AgeVerificationFormProps {
  defaultData?: YouthMembershipFormData;
  showEditModal?: boolean;
  defaultImages?: ImageUrls;
  isAdmin?: boolean;
  handleNext: () => void;
  onBack?: () => void;
  onStatusUpdated?: (status: import("../../../api/earlyStageMembershipApi").EarlyStageVerificationStatus) => void;
}

function AgeVerificationFormInner({
  showEditModal,
  defaultData,
  isAdmin,
  defaultImages,
  handleNext,
  onBack,
  onStatusUpdated,
}: AgeVerificationFormProps) {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    control,
    setError,
    clearErrors,
    formState: { errors, isDirty },
  } = useForm({
    resolver: yupResolver(ageVerificationSchema),
    defaultValues: {
      idDocument: defaultImages?.IdDocument ?? "",
      phoneNumber: defaultData?.phoneNumber ?? "",
      city: defaultData?.city ?? "",
      country: defaultData?.country ?? "",
      dateOfBirth: defaultData?.dateOfBirth
        ? new Date(defaultData.dateOfBirth)
        : new Date(),
    },
  });
  const { objectId } = useAppContext();
  const { data: userData } = useGetUser({ id: objectId || "" });
  const queryClient = useQueryClient();
  const idUploadTooltip = useMemo(
    () => renderTooltip(ID_UPLOAD_TOOLTIP_MSG),
    []
  );
  const { mutate: updateUser, isPending } = useSaveUser({
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["user", objectId] });
      toast.success(t("feedback.userUpdatedSuccess"));
      handleNext()
    },
    onError: () => {
      toast.error(t("feedback.userUpdateFailed"));
    },
  });

  const membershipNameFromDb = userData?.user?.membershipName ?? "";
  const membershipNameFromDraft = useMemo(() => {
    try {
      const raw = localStorage.getItem("UserData");
      if (!raw) return "";
      const parsed = JSON.parse(raw) as { membershipName?: string };
      return typeof parsed.membershipName === "string"
        ? parsed.membershipName
        : "";
    } catch {
      return "";
    }
  }, []);
  const membershipName = membershipNameFromDb || membershipNameFromDraft;
  const useEarlyStageApi = isEarlyStageMembership(membershipName);

  const onSubmit = useCallback(
    async (data: FormType) => {
      if (errors.idDocument) {
        setError("idDocument", {
          type: "manual",
          message: errors.idDocument.message,
        });
        return;
      }

      const idDocumentFile = (
        document.getElementById("idUpload") as HTMLInputElement
      )?.files?.[0];

      if (useEarlyStageApi) {
        const dobIso = toDateOnlyString(data.dateOfBirth);
        const needsId = isUnder23(data.dateOfBirth);
        if (needsId && !idDocumentFile && !defaultImages?.IdDocument) {
          setError("idDocument", {
            type: "manual",
            message: "ID upload is required for the Young Professional rate.",
          });
          return;
        }
        try {
          const status = await submitEarlyStageVerification({
            ageTier: isUnder23(data.dateOfBirth) ? "Under23" : "TwentyThreePlus",
            dateOfBirth: dobIso,
            idDocument: idDocumentFile ?? null,
            phoneNumber: data.phoneNumber ?? undefined,
            city: data.city ?? undefined,
            country: data.country ?? undefined,
          });
          await queryClient.invalidateQueries({ queryKey: ["user", objectId] });
          onStatusUpdated?.(status);
          if (status.requiresAdminApproval) {
            toast.info(
              "Your ID has been submitted. An admin will review it before you can pay."
            );
          } else if (status.status === "Approved") {
            toast.success(
              "Age verified. You can continue with £10/month membership."
            );
          }
          handleNext();
        } catch {
          toast.error("Could not submit age verification.");
        }
        return;
      }

      if (!isDirty) {
        handleNext();
        return;
      }

      const formData = new FormData();
      const mergedData = {
        ...userData?.user,
        ...data,
        dateOfBirth: data.dateOfBirth.toISOString(),
        phoneNumber: data.phoneNumber || "",
        city: data.city || "",
        country: data.country || "",
      };

      Object.entries(mergedData).forEach(([key, value]) => {
        if (
          value !== null &&
          value !== undefined &&
          key !== "idDocument" &&
          key !== "professionalPhoto"
        ) {
          if (typeof value === "object") {
            formData.append(key, JSON.stringify(value));
          } else {
            formData.append(key, String(value));
          }
        }
      });

      if (idDocumentFile) {
        formData.append("idDocument", idDocumentFile);
      }

      localStorage.setItem("UserProfile", JSON.stringify(mergedData));
      updateUser(formData);
    },
    [
      errors.idDocument,
      isDirty,
      userData?.user,
      handleNext,
      updateUser,
      setError,
      useEarlyStageApi,
      defaultImages?.IdDocument,
      objectId,
      queryClient,
    ]
  );

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <Row
        className="mb-4 shadow-sm rounded-4 py-3"
        style={{ minHeight: FORM_MIN_HEIGHT }}
      >
        <Col md={12}>
          <SectionHeader
            title="Age Verification Information"
            tooltip={
              useEarlyStageApi
                ? "We use your date of birth and ID to confirm eligibility for Early Stage membership. Young Professional (ages 18–22): upload ID (PNG, JPG, or PDF) for admin review, then choose £2 or £10/month. Age 23+: you can pay £10/month after verification."
                : "We ask for your date of birth and ID upload to confirm eligibility. Your ID is stored securely for admin verification."
            }
          />
        </Col>
        <Col md={6}>
        <Form.Label>Date of Birth*</Form.Label>
          <Controller
            control={control}
            name="dateOfBirth"
            render={({ field }) => (
              <Form.Control
                type="date"
                disabled={showEditModal && !isAdmin}
                value={
                  field.value
                    ? new Date(field.value).toISOString().split("T")[0]
                    : ""
                }
                onChange={(e) => {
                  const dateValue = e.target.value
                    ? new Date(e.target.value)
                    : null;
                  field.onChange(dateValue);
                }}
                isInvalid={!!errors.dateOfBirth}
              />
            )}
          />
          {errors.dateOfBirth && (
            <Form.Control.Feedback
              className="custom-error-text px-1"
              type="invalid"
            >
              {errors.dateOfBirth.message}
            </Form.Control.Feedback>
          )}
        </Col>
        <Col md={6}>
  <Form.Group controlId="idUpload">
    <OverlayTrigger placement="right" overlay={idUploadTooltip}>
      <Form.Label>ID Upload*</Form.Label>
    </OverlayTrigger>
    <Form.Control
      type="file"
      accept=".png,.jpg,.jpeg,.pdf,.heif,.heic"
      onChange={(e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (file) {
          if (!VALID_ID_TYPES.includes(file.type)) {
            setError("idDocument", {
              type: "manual",
              message: "Unsupported File Format",
            });
            return;
          }

          if (file.size > MAX_ID_FILE_SIZE) {
            setError("idDocument", {
              type: "manual",
              message: "File size must be less than 5MB",
            });
            return;
          }

          const reader = new FileReader();
          reader.onloadend = () => {
            const base64String = reader.result?.toString();
            if (base64String) {
              clearErrors("idDocument");
              const event = {
                target: {
                  name: "idDocument",
                  value: base64String,
                },
              };
              register("idDocument").onChange(event);
            } else {
              setError("idDocument", {
                type: "manual",
                message: "File upload failed. Please try again.",
              });
            }
          };
          reader.onerror = () => {
            setError("idDocument", {
              type: "manual",
              message: "File upload failed. Please try again.",
            });
          };

          reader.readAsDataURL(file);
        }
      }}
      isInvalid={!!errors.idDocument}
    />
    <Form.Control.Feedback
      className="custom-error-text px-1"
      type="invalid"
    >
      {errors.idDocument?.message}
    </Form.Control.Feedback>
  </Form.Group>
</Col>

        <Col className="mt-4" md={12}>
          <SectionHeader
            title="Contact Information"
            tooltip="Sharing your city and country helps us connect you with mentors and peers in your region. A phone number is optional but allows additional contact options for networking opportunities."
          />
        </Col>
        <Col md={4}>
          <Form.Group controlId="phoneNumber">
            <Form.Label>Phone Number*</Form.Label>
            <div className="form-control p-0">
              <Controller
                control={control}
                name="phoneNumber"
                render={({ field }) => (
                  <PhoneInput
                    {...field}
                    disabled={showEditModal && !isAdmin}
                    placeholder="Enter phone number"
                    defaultCountry="GB"
                    value={field.value || ""}
                    onChange={field.onChange}
                    className="phone-input w-100"
                  />
                )}
              />
            </div>
            {errors.phoneNumber && (
              <div className="custom-error-text px-1 ">
                {errors.phoneNumber.message}
              </div>
            )}
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group controlId="city">
            <Form.Label>City*</Form.Label>
            <Form.Control
              disabled={showEditModal && !isAdmin}
              type="text"
              {...register("city")}
              isInvalid={!!errors.city}
            />
            <Form.Control.Feedback
              className="custom-error-text px-1"
              type="invalid"
            >
              {errors.city?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group controlId="country">
            <SelectInput
              name="country"
              label="Country"
              placeholder="Select Country"
              control={control}
              options={countryOptions}
              error={errors.country}
              isRequired
              disabled={showEditModal && !isAdmin}
            />
          </Form.Group>
        </Col>
        <Col>
          <div className="d-flex justify-content-between gap-2">
            {onBack ? (
              <Button type="button" variant="outline-secondary" onClick={onBack}>
                Back
              </Button>
            ) : (
              <span />
            )}
            <Button disabled={isPending} variant="primary" type="submit">
              {isPending ? "Saving..." : "Save & Continue"}
            </Button>
          </div>
        </Col>
      </Row>
    </Form>
  );
}

AgeVerificationFormInner.displayName = "AgeVerificationForm";

export default memo(AgeVerificationFormInner);
